// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from "react";
import { DEMO_ACCOUNTS } from "../data/mockData";

// Set USE_DEMO = true for mock mode (no Firebase needed)
// Set USE_DEMO = false to use real Firebase Auth
export const USE_DEMO = true;

const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (USE_DEMO) {
      // Check localStorage for persisted demo session
      const saved = localStorage.getItem("sp_demo_user");
      if (saved) {
        const user = JSON.parse(saved);
        setCurrentUser(user);
        setUserRole(user.role);
      }
      setLoading(false);
    } else {
      // Real Firebase auth listener
      const { auth } = require("../firebase");
      const { onAuthStateChanged } = require("firebase/auth");
      const { doc, getDoc } = require("firebase/firestore");
      const { db } = require("../firebase");

      const unsubscribe = onAuthStateChanged(auth, async (user) => {
        if (user) {
          const docRef = doc(db, "users", user.uid);
          const docSnap = await getDoc(docRef);
          const role = docSnap.exists() ? docSnap.data().role : "student";
          setCurrentUser({ ...user, name: user.displayName || user.email });
          setUserRole(role);
        } else {
          setCurrentUser(null);
          setUserRole(null);
        }
        setLoading(false);
      });
      return unsubscribe;
    }
  }, []);

  async function login(email, password) {
    if (USE_DEMO) {
      const account = DEMO_ACCOUNTS[email.toLowerCase()];
      if (!account || account.password !== password) {
        throw new Error("Invalid email or password.");
      }
      const user = { email, name: account.name, role: account.role, uid: email };
      localStorage.setItem("sp_demo_user", JSON.stringify(user));
      setCurrentUser(user);
      setUserRole(account.role);
      return user;
    } else {
      const { auth } = require("../firebase");
      const { signInWithEmailAndPassword } = require("firebase/auth");
      return signInWithEmailAndPassword(auth, email, password);
    }
  }

  async function logout() {
    if (USE_DEMO) {
      localStorage.removeItem("sp_demo_user");
      setCurrentUser(null);
      setUserRole(null);
    } else {
      const { auth } = require("../firebase");
      const { signOut } = require("firebase/auth");
      return signOut(auth);
    }
  }

  const value = { currentUser, userRole, login, logout, loading };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
